import { Log, SigninRequest, User, UserManager } from "oidc-client";
import React, { useContext } from "react";
import { fetchAPI } from "../utils/api";
import { IDENTITY_CONFIG, METADATA_OIDC } from "../utils/authConst";
import { useLocalStorage } from "../utils/useLocalStorage";
import { addNotification } from "../utils/Utils";

export type AuthType = {
  user: User | null;
  role: any;
  signinRedirect(): Promise<void>;
  signinRedirectCallback(): Promise<User>;
  signinSilent(): void;
  signinSilentCallback(): Promise<User>;
  logout(): void;
  signoutRedirectCallback(): void;
  createSigninRequest(): Promise<SigninRequest>;
  isAuthenticated(): boolean;
};

const useAuthProvider = () => {
  let userManager = new UserManager({
    ...IDENTITY_CONFIG,
    metadata: {
      ...METADATA_OIDC
    }
  })

  const [user, setUser] = useLocalStorage("user");
  const [role, setRole] = useLocalStorage('role');

  // Change the logger to console logger
  Log.logger = console;
  Log.level = Log.WARN;

  userManager.events.addUserLoaded(async (user) => {
    setUser(user);
    getRule();
  });

  userManager.events.addSilentRenewError((e) => {
    console.log("silent renew error", e.message);
  });

  userManager.events.addAccessTokenExpired(() => {
    console.log("token expired");
    signinSilent();
    // logout();
  });

  userManager.events.removeUserLoaded(() => {
    setRole(null)
    setUser(null)
  })

  const getRule = async () => {
    try {
      return await fetchAPI(`/users/me`, {
        method: "GET"
      }).then((res) => {
        return res.json();
      }).then((r) => {
        setRole(r.role);
      })
    } catch (error) {
      console.error("Error occurs during retrieving rule, cause", error);
      addNotification('warning', "Error occurs retrieving user role")
      return null;
    }
  };

  const signinRedirectCallback = () => {
    return userManager.signinRedirectCallback();
  };

  const signinRedirect = (): Promise<void> => {
    userManager.clearStaleState();
    localStorage.setItem("redirectUri", window.location.pathname);
    return userManager.signinRedirect({ state: window.location.href });
  };

  const isAuthenticated = (): boolean => {
    return user !== null
        && user !== undefined
        && user.access_token !== null
        && user.expires_at > ((new Date().getTime() + 1) / 1000);
  };

  const signinSilent = (): void => {
    userManager
      .signinSilent()
      .then((user) => {
        console.log("signed in", user);
        setUser(user);
      })
      .catch(() => {
        userManager.clearStaleState();
      });
  };
  const signinSilentCallback = () => {
    return userManager.signinSilentCallback();
  };

  const createSigninRequest = (): Promise<SigninRequest> => {
    return userManager.createSigninRequest();
  };

  const logout = (): void => {
    userManager.signoutRedirect({
      id_token_hint: localStorage.getItem("id_token"),
    });
    userManager.clearStaleState();
  };

  const signoutRedirectCallback = (): void => {
    const onecommConfigurations = window['onecommConfigurations' as any] as any

    userManager.signoutRedirectCallback().then(() => {
      localStorage.clear();
      if (onecommConfigurations.REACT_APP_PUBLIC_URL && onecommConfigurations.REACT_APP_BASEPATH)
        window.location.replace((onecommConfigurations.REACT_APP_PUBLIC_URL + onecommConfigurations.REACT_APP_BASEPATH).toString());
      else console.log("Undefined REACT_APP_PUBLIC_URL variable");
    });
    userManager.clearStaleState();
  };

  return {
    user,
    role,
    signinRedirect,
    signinRedirectCallback,
    signinSilent,
    signinSilentCallback,
    logout,
    signoutRedirectCallback,
    createSigninRequest,
    isAuthenticated,
  };
};

export const AuthContext = React.createContext<any>(null);

export const AuthProvider = ({ children }: any) => {
  const authService = useAuthProvider();

  return <AuthContext.Provider value={authService}>{children}</AuthContext.Provider>;
};

const useAuth = () => {
  return useContext(AuthContext);
};

export default useAuth;